import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ShoppingBag,
  ArrowRight,
  Phone,
  ArrowLeft,
  Filter,
  PackageOpen,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const [active, setActive] = useState(initialCategory);

  // keep URL in sync (active -> URL)
  useEffect(() => {
    if (active === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", active);
    }
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // sync URL -> active (handles dropdown clicks without remount)
  useEffect(() => {
    const cat = searchParams.get("category") || "all";
    if (cat !== active) setActive(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const products = useMemo(() => {
    const all = SITE.shop?.products || [];
    return active === "all"
      ? all
      : all.filter((p) => p.category === active);
  }, [active]);

  const activeCat =
    SITE.shop.categories.find((c) => c.slug === active) || null;

  useDocumentMeta({
    title: activeCat
      ? `${activeCat.name} | Shop | ${SITE.business.name}`
      : `Shop | ${SITE.business.name}`,
    description: activeCat
      ? `${activeCat.desc} Sunshine Coast pickup or installed by ${SITE.business.name}.`
      : `Browse Haltech, 4WD accessories and towing electrics at ${SITE.business.fullName}. Pick up at our Kunda Park workshop or have it fitted on-site.`,
    canonical: `https://powerzautoelectrical.com.au/shop${active === "all" ? "" : "?category=" + active}`,
  });

  return (
    <div className="bg-black text-white">
      <Header />

      {/* Hero */}
      <section
        data-testid="shop-hero"
        className="relative w-full overflow-hidden bg-black pt-40 md:pt-48 pb-16"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${SITE.hero.image})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 60%, #000 100%)",
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
          <Link
            to="/"
            data-testid="shop-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            BACK TO HOME
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              POWERZ SHOP
            </span>
          </div>
          <h1
            data-testid="shop-headline"
            className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-4xl"
          >
            GEAR THAT <br />
            <span className="text-cyan-pz glow-cyan">GETS USED.</span>
          </h1>
          <p className="mt-8 text-white/80 barlow text-lg md:text-xl max-w-3xl leading-relaxed">
            Haltech engine management, 4WD auto electrical accessories and towing
            electrics — all available for pickup at our Kunda Park workshop or
            fitted on-site by our team.
          </p>
        </div>
      </section>

      {/* Category filter */}
      <section className="relative bg-[#0A0A0A] border-y border-white/10 py-6 sticky top-24 md:top-32 lg:top-40 z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-center gap-3">
          <span className="hidden md:inline-flex items-center gap-2 text-white/50 bebas tracking-widest text-sm">
            <Filter className="w-4 h-4" />
            CATEGORY
          </span>
          <FilterChip
            active={active === "all"}
            onClick={() => setActive("all")}
            testid="filter-all"
          >
            All
          </FilterChip>
          {SITE.shop.categories.map((c) => (
            <FilterChip
              key={c.slug}
              active={active === c.slug}
              onClick={() => setActive(c.slug)}
              testid={`filter-${c.slug}`}
            >
              {c.name}
            </FilterChip>
          ))}
        </div>
      </section>

      {/* Active category description */}
      {activeCat && (
        <section className="bg-black py-10 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="bebas text-cyan-pz tracking-[0.25em] text-xs">
                {activeCat.kicker}
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-3">
              {activeCat.name.toUpperCase()}
            </h2>
            <p className="text-white/70 barlow max-w-3xl">{activeCat.desc}</p>
          </div>
        </section>
      )}

      {/* Products grid OR empty state */}
      <section
        id="shop-products"
        className="relative bg-black py-16 md:py-20 min-h-[40vh]"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {products.length === 0 ? (
            <EmptyState category={activeCat} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {products.map((p) => (
                <article
                  key={p.slug}
                  data-testid={`product-${p.slug}`}
                  className="group bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-all overflow-hidden clip-perf"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-black">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PackageOpen className="w-12 h-12 text-white/20" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {p.brand && (
                      <p className="bebas text-cyan-pz/70 tracking-widest text-xs mb-2">
                        {p.brand}
                      </p>
                    )}
                    <h3 className="bebas text-white text-2xl tracking-wide mb-2 leading-tight">
                      {p.name}
                    </h3>
                    {p.short && (
                      <p className="text-white/60 barlow text-sm mb-4">
                        {p.short}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="bebas text-cyan-pz text-2xl">
                        {p.price || "POA"}
                      </span>
                      <a
                        href="#contact"
                        className="inline-flex items-center gap-1 bebas tracking-widest text-sm text-white hover:text-cyan-pz transition-colors"
                      >
                        ENQUIRE
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`bebas tracking-widest text-sm px-5 py-2.5 transition-all clip-tag border ${
        active
          ? "bg-cyan-pz text-black border-cyan-pz"
          : "bg-transparent text-white border-white/20 hover:border-cyan-pz hover:text-cyan-pz"
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ category }) {
  return (
    <div
      data-testid="shop-empty-state"
      className="text-center max-w-2xl mx-auto py-12"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 border border-cyan-pz/40 bg-cyan-pz/5 mb-8">
        <ShoppingBag className="w-9 h-9 text-cyan-pz" />
      </div>
      <h3 className="bebas text-white text-3xl md:text-5xl tracking-wider mb-4">
        {category ? category.name.toUpperCase() : "STOCK"} ARRIVING SOON
      </h3>
      <p className="text-white/60 barlow text-base md:text-lg mb-8">
        We're building out the online catalogue. In the meantime — if you're
        after specific gear, give us a bell and we'll source it for you and
        give you a price on the spot.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href={`tel:${SITE.business.phoneTel}`}
          data-testid="shop-empty-call-btn"
          className="inline-flex items-center justify-center gap-2 bg-cyan-pz text-black px-7 py-4 bebas tracking-widest hover:bg-white transition-colors clip-perf"
        >
          <Phone className="w-4 h-4" />
          CALL {SITE.business.phone}
        </a>
        <a
          href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20Shop%20Enquiry`}
          data-testid="shop-empty-enquire-btn"
          className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-7 py-4 bebas tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
        >
          EMAIL US
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
